import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { classNames } from "primereact/utils";
import styles from "./EventDetailsContent.module.scss";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { PriceDisplay } from "../../../../common/components/PriceDisplay";
import { MultisportTag } from "../../../../common/components/common/MultisportTag/MultisportTag";
import {
  IEventDetailsContentProps,
  useEventDetailsContent,
} from "../hooks/useEventDetailsContent";

const DetailIcon = ({ type }: { type: IconType }) => (
  <Icon type={type} color="currentColor" height={20} width={20} />
);

const FactTile = ({
  icon,
  label,
  value,
}: {
  icon: IconType;
  label: string;
  value: ReactNode;
}) => (
  <div className={styles.fact}>
    <div className={styles.fact_icon} aria-hidden="true">
      <DetailIcon type={icon} />
    </div>
    <div className={styles.fact_text}>
      <span className={styles.fact_label}>{label}</span>
      <span className={styles.fact_value}>{value}</span>
    </div>
  </div>
);

const PlaceRow = ({
  icon,
  children,
}: {
  icon: IconType;
  children: ReactNode;
}) => (
  <div className={styles.place}>
    <div className={styles.place_icon} aria-hidden="true">
      <DetailIcon type={icon} />
    </div>
    <div className={styles.place_text}>{children}</div>
  </div>
);

const MetaRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className={styles.meta_row}>
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
);

export const EventDetailsContent = (props: IEventDetailsContentProps) => {
  const {
    eventName,
    reservationId,
    instructor,
    dateLabel,
    timeLabel,
    address,
    mapsLink,
    phoneNumber,
    phoneNumberDescription,
    secondaryPhoneNumber,
    secondaryPhoneNumberDescription,
    description,
    pricePaid,
    currencyId,
    warningMessage,
    price,
    meetingUrl,
  } = props;

  const {
    accentStyle,
    showMultisport,
    isMultisportAccepted,
    showInstructor,
    showWarning,
    reservationRows,
    showReservationSection,
    reservationSectionTitle,
    showReservationMeta,
    showPaymentType,
    paymentTypeLabel,
    showPaidAmount,
    showVisits,
    visitsUsedLabel,
    studioNameLabel,
    showStudioName,
    showAddress,
    showPhone,
    showSecondaryPhone,
    showPlaceSection,
    showDescription,
    showMeeting,
    hasMeetingLink,
  } = useEventDetailsContent(props);

  return (
    <div className={styles.content} style={accentStyle}>
      <header className={styles.hero}>
        <h3 className={styles.hero_title}>{eventName}</h3>
        <RenderWhen condition={showMultisport}>
          <div className={styles.hero_tags}>
            <MultisportTag isAccepted={isMultisportAccepted} showStatus />
          </div>
        </RenderWhen>
      </header>

      <div className={styles.facts}>
        <FactTile icon={IconType.Calendar} label="Дата" value={dateLabel} />
        <FactTile icon={IconType.ClockIcon} label="Час" value={timeLabel} />
        <RenderWhen condition={showInstructor}>
          <FactTile
            icon={IconType.Person}
            label="Инструктор"
            value={instructor}
          />
        </RenderWhen>
        <FactTile
          icon={IconType.Dollar3}
          label="Цена за посещение"
          value={<PriceDisplay minPrice={price || 0} />}
        />
      </div>

      <RenderWhen condition={showReservationSection}>
        <section className={styles.card}>
          <h4 className={styles.card_title}>{reservationSectionTitle}</h4>

          <RenderWhen condition={reservationRows.length > 0}>
            <ul className={styles.statuses}>
              {reservationRows.map((row) => (
                <li
                  key={row.key}
                  className={classNames(
                    styles.status,
                    styles[`status_${row.tone}`],
                  )}
                >
                  <span className={styles.status_icon} aria-hidden="true">
                    <DetailIcon type={row.icon} />
                  </span>
                  <span className={styles.status_text}>{row.text}</span>
                </li>
              ))}
            </ul>
          </RenderWhen>

          <RenderWhen condition={showReservationMeta}>
            <dl className={styles.meta}>
              <RenderWhen condition={!!reservationId}>
                <MetaRow label="Номер на резервацията" value={reservationId} />
              </RenderWhen>
              <RenderWhen condition={showPaymentType}>
                <MetaRow label="Начин на плащане" value={paymentTypeLabel} />
              </RenderWhen>
              <RenderWhen condition={showPaidAmount}>
                <MetaRow
                  label="Платена сума"
                  value={
                    <PriceDisplay minPrice={pricePaid!} currencyId={currencyId} />
                  }
                />
              </RenderWhen>
              <RenderWhen condition={showVisits}>
                <MetaRow label="Поредно посещение" value={visitsUsedLabel} />
              </RenderWhen>
            </dl>
          </RenderWhen>
        </section>
      </RenderWhen>

      <RenderWhen condition={showMeeting}>
        <section className={styles.card}>
          <h4 className={styles.card_title}>Дистанционна среща</h4>

          <RenderWhen condition={hasMeetingLink}>
            <div className={styles.meeting}>
              {/* The link is typed by Google, but the new tab still must not get a handle on ours. */}
              <a
                className={styles.meeting_join}
                href={meetingUrl!}
                target="_blank"
                rel="noopener noreferrer"
              >
                Влез в срещата
              </a>
              <a
                className={styles.meeting_url}
                href={meetingUrl!}
                target="_blank"
                rel="noopener noreferrer"
              >
                {meetingUrl}
              </a>
            </div>
          </RenderWhen>

          <RenderWhen condition={!hasMeetingLink}>
            <p className={styles.meeting_hint}>
              Срещата ще бъде онлайн, през Google Meet. Линкът ще бъде тук и в имейла за потвърждение.
            </p>
          </RenderWhen>
        </section>
      </RenderWhen>

      <RenderWhen condition={showWarning}>
        <div className={styles.warning} role="note">
          <span className={styles.warning_icon} aria-hidden="true">
            <DetailIcon type={IconType.Warning} />
          </span>
          <p>{warningMessage}</p>
        </div>
      </RenderWhen>

      <RenderWhen condition={showPlaceSection}>
        <section className={styles.card}>
          <h4 className={styles.card_title}>Място и контакт</h4>
          <div className={styles.places}>
            <RenderWhen condition={showStudioName}>
              <PlaceRow icon={IconType.Store}>{studioNameLabel}</PlaceRow>
            </RenderWhen>
            <RenderWhen condition={showAddress}>
              <PlaceRow icon={IconType.Location}>
                {/* The studio types this link itself, so the new tab must not get a handle on ours. */}
                <Link to={mapsLink!} target="_blank" rel="noopener noreferrer">
                  {address}
                </Link>
              </PlaceRow>
            </RenderWhen>
            <RenderWhen condition={showPhone}>
              <PlaceRow icon={IconType.Phone}>
                <Link to={`tel:${phoneNumber}`}>{phoneNumber}</Link>
                <RenderWhen condition={!!phoneNumberDescription}>
                  <span className={styles.place_hint}>
                    {phoneNumberDescription}
                  </span>
                </RenderWhen>
              </PlaceRow>
            </RenderWhen>
            <RenderWhen condition={showSecondaryPhone}>
              <PlaceRow icon={IconType.Phone}>
                <Link to={`tel:${secondaryPhoneNumber}`}>
                  {secondaryPhoneNumber}
                </Link>
                <RenderWhen condition={!!secondaryPhoneNumberDescription}>
                  <span className={styles.place_hint}>
                    {secondaryPhoneNumberDescription}
                  </span>
                </RenderWhen>
              </PlaceRow>
            </RenderWhen>
          </div>
        </section>
      </RenderWhen>

      <RenderWhen condition={showDescription}>
        <section className={styles.card}>
          <h4 className={styles.card_title}>Описание</h4>
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{ __html: description! }}
          />
        </section>
      </RenderWhen>
    </div>
  );
};
